import { useCallback, useEffect, useRef, useState } from 'react';
import { actionBufferTimeoutMs } from '../helpers/with-timeout.helpers';
import { useScoreBufferFailureSnackbar } from './use-score-buffer-failure-snackbar';
import { useScoreStillSavingSnackbar } from './use-score-still-saving-snackbar';

interface IScoreAttempt {
  buffered: Promise<void>;
  settled: boolean;
  slowTimer?: ReturnType<typeof setTimeout>;
}

export const usePendingScoreBuffer = () => {
  const reportScoreBufferFailure = useScoreBufferFailureSnackbar();
  const setScoreStillSaving = useScoreStillSavingSnackbar();
  const [pendingScoreKeys, setPendingScoreKeys] = useState<Set<string>>(new Set());
  // The latest write per cell; an older attempt's outcome never touches a cell a newer one owns.
  const latestAttempts = useRef(new Map<string, IScoreAttempt>());
  // Latest attempts still pending past one storage bound; one shared notice shows while any remain.
  const slowAttempts = useRef(new Set<IScoreAttempt>());

  const markSlow = useCallback((attempt: IScoreAttempt, slow: boolean) => {
    const wasShowing = slowAttempts.current.size > 0;
    if (slow) slowAttempts.current.add(attempt);
    else slowAttempts.current.delete(attempt);
    const showing = slowAttempts.current.size > 0;
    if (showing !== wasShowing) setScoreStillSaving(showing);
  }, [setScoreStillSaving]);

  const retire = useCallback((attempt: IScoreAttempt) => {
    if (attempt.slowTimer !== undefined) clearTimeout(attempt.slowTimer);
    markSlow(attempt, false);
  }, [markSlow]);

  const settle = useCallback((key: string, attempt: IScoreAttempt) => {
    if (latestAttempts.current.get(key) !== attempt) return false;
    attempt.settled = true;
    setPendingScoreKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    return true;
  }, []);

  const track = useCallback((key: string, buffered: Promise<void>, onRejected: (priorWrite?: Promise<void>) => void) => {
    buffered.catch(reportScoreBufferFailure);
    const previous = latestAttempts.current.get(key);
    const priorWrite = previous && !previous.settled ? previous.buffered : undefined;
    if (previous) retire(previous);
    const attempt: IScoreAttempt = { buffered, settled: false };
    // Starts at the tap, so a write queued behind a slow one is covered too.
    attempt.slowTimer = setTimeout(() => {
      if (latestAttempts.current.get(key) === attempt && !attempt.settled) markSlow(attempt, true);
    }, actionBufferTimeoutMs());
    latestAttempts.current.set(key, attempt);
    setPendingScoreKeys(prev => new Set(prev).add(key));
    buffered.then(
      () => { retire(attempt); settle(key, attempt); },
      () => { retire(attempt); if (settle(key, attempt)) onRejected(priorWrite); },
    );
    return attempt;
  }, [reportScoreBufferFailure, retire, markSlow, settle]);

  const trackScoreWrite = useCallback((key: string, buffered: Promise<void>, revert: () => void) => {
    track(key, buffered, revert);
  }, [track]);

  // A failed removal puts the score back only once that score's own write is known to have landed.
  const trackScoreRemoval = useCallback((key: string, buffered: Promise<void>, restore: () => void) => {
    const attempt = track(key, buffered, (priorWrite) => {
      (priorWrite ?? Promise.resolve()).then(
        () => { if (latestAttempts.current.get(key) === attempt) restore(); },
        () => console.warn(`score removal ${key}: the removed score was never stored, so the cell stays empty`),
      );
    });
  }, [track]);

  const resetPendingScores = useCallback(() => {
    latestAttempts.current.forEach(retire);
    latestAttempts.current.clear();
    setPendingScoreKeys(new Set());
  }, [retire]);

  // The notice is app-wide, so it can't outlive the scoring screen that raised it.
  const retireRef = useRef(retire);
  retireRef.current = retire;
  useEffect(() => {
    const attempts = latestAttempts.current;
    return () => attempts.forEach((attempt) => retireRef.current(attempt));
  }, []);

  return { pendingScoreKeys, trackScoreWrite, trackScoreRemoval, resetPendingScores };
};
